import { useEffect, useState } from "react";
import {
  checkRangePublicDomain,
  disconnectRangePublicDomain,
  getRangePublicDomain,
  setupRangePublicDomain
} from "../lib/rangegridApi";

const DAY_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const SETTINGS_SECTION_IDS = ["profile", "domain", "hours", "member-portal", "workspace-menu"];
const CONSOLE_ICON_INPUT_LIMIT_BYTES = 2 * 1024 * 1024;
const CONSOLE_ICON_OUTPUT_LIMIT_CHARS = 260000;
const OPTIONAL_OWNER_APPS = [
  {
    key: "armory",
    label: "Armory",
    detail: "Range-owned firearms, round counts, checkout, return, and firearm service workflows."
  },
  {
    key: "leads",
    label: "Leads",
    detail: "Public website inquiries and follow-up for ranges using the public interest form."
  },
  {
    key: "reports",
    label: "Reports",
    detail: "Lightweight owner-safe CSV exports and operational snapshots."
  },
  {
    key: "audit",
    label: "Audit",
    detail: "Activity history for owners who need a visible accountability trail."
  }
];
const MEMBER_PORTAL_ACTIONS = [
  {
    key: "membershipStatus",
    label: "Membership status",
    detail: "Current plan, review state, expiration, and activation status.",
    defaultEnabled: true
  },
  {
    key: "waiver",
    label: "Release form",
    detail: "Waiver status and signing action when a member needs to acknowledge a release.",
    defaultEnabled: true
  },
  {
    key: "onboarding",
    label: "Orientation and onboarding",
    detail: "Range orientation, background-check, and approval follow-up steps.",
    defaultEnabled: true
  },
  {
    key: "qualifications",
    label: "Qualifications and gated access",
    detail: "Approved qualifications, pending requests, access-card state, and restricted range gates.",
    defaultEnabled: true
  },
  {
    key: "billing",
    label: "Billing and renewals",
    detail: "Subscription cadence, renewal, plan-change, and checkout follow-up actions.",
    defaultEnabled: true
  },
  {
    key: "reservations",
    label: "Lane and bay reservations",
    detail: "Member booking entry points when resources are published for member booking.",
    defaultEnabled: true
  },
  {
    key: "events",
    label: "Programs and events",
    detail: "Class, orientation, match, and event registration entry points.",
    defaultEnabled: true
  },
  {
    key: "guestPasses",
    label: "Guest passes",
    detail: "Guest-pass redemption and guest waiver follow-up when a plan supports it.",
    defaultEnabled: true
  },
  {
    key: "rentals",
    label: "Rental equipment",
    detail: "Future rental workflows without exposing unfinished armory tools.",
    defaultEnabled: false
  }
];

function titleCase(value) {
  return String(value || "").replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());
}

function formatDayWindow(day) {
  const period = day?.periods?.[0];
  if (!day || day.closed) {
    return "Closed";
  }
  return `${formatPeriodPoint(period, "opens")} to ${formatPeriodPoint(period, "closes")}`;
}

function displayValue(value, fallback = "Not set") {
  if (value === null || value === undefined) {
    return fallback;
  }
  const normalized = String(value).trim();
  return normalized || fallback;
}

function domainStatusLabel(status = "") {
  const labels = {
    not_connected: "Not connected",
    preparing_setup: "Preparing setup",
    waiting_nameserver_update: "Waiting for nameserver update",
    checking_dns: "Checking DNS",
    issuing_certificate: "Issuing certificate",
    connecting_website: "Connecting website",
    live: "Live",
    needs_attention: "Needs attention"
  };
  return labels[status] || labels.not_connected;
}

function domainStatusDetail(status = "") {
  const details = {
    not_connected: "Add the domain where members and prospects should find this public website.",
    preparing_setup: "RangeGrid is preparing the domain setup.",
    waiting_nameserver_update: "Change nameservers in GoDaddy, then ask RangeGrid to check again.",
    checking_dns: "RangeGrid is checking whether the nameserver change is visible.",
    issuing_certificate: "RangeGrid is waiting for HTTPS to finish issuing.",
    connecting_website: "RangeGrid is connecting the website edge network.",
    live: "Your website is live on this domain.",
    needs_attention: "RangeGrid needs one issue fixed before this domain can go live."
  };
  return details[status] || details.not_connected;
}

function domainConfigStatus(config = null) {
  return config?.status || "not_connected";
}

function visibleDomainConfig(publicDomain = {}) {
  return publicDomain.pending || publicDomain.active || null;
}

function publicContactLooksInternal(value = "") {
  return /(?:@dev\.rangegrid\.local|rangegrid-.*audit|owner-audit|test@|example\.com|\.example(?:\/|$)|555[-\s.]?01\d{2})/i.test(String(value || ""));
}

function addressLooksInternal(address = {}) {
  return /(?:100\s+Chapter\s+House\s+Way|Los Angeles,\s*CA,\s*90012|Example|Demo|Test)/i.test(addressSummary(address));
}

function loxPublicContactFallback(selectedRange = {}) {
  const name = [selectedRange.name, selectedRange.profile?.displayName, selectedRange.slug]
    .filter(Boolean)
    .join(" ");
  if (!/lox|loaded/i.test(name)) {
    return {};
  }
  return {
    contactEmail: "info@loxandloaded.org",
    contactPhone: "1 (334) 569-5623",
    website: "https://loxandloaded.org/"
  };
}

function loxPublicAddressFallback(selectedRange = {}) {
  const name = [selectedRange.name, selectedRange.profile?.displayName, selectedRange.slug]
    .filter(Boolean)
    .join(" ");
  if (!/lox|loaded/i.test(name)) {
    return {};
  }
  return {
    line1: "",
    line2: "",
    city: "",
    region: "",
    postalCode: "",
    country: "US"
  };
}

function publicContactDraft(form = {}, selectedRange = {}) {
  const fallback = loxPublicContactFallback(selectedRange);
  return {
    contactEmail: form.contactEmail && !publicContactLooksInternal(form.contactEmail) ? form.contactEmail : fallback.contactEmail || "",
    contactPhone: form.contactPhone && !publicContactLooksInternal(form.contactPhone) ? form.contactPhone : fallback.contactPhone || "",
    website: form.website && !publicContactLooksInternal(form.website) ? form.website : fallback.website || ""
  };
}

function displayTimezone(value, fallback = "Not set") {
  const normalized = displayValue(value, fallback);
  return normalized === fallback ? fallback : normalized.replace(/^America\//, "").replace(/_/g, " ");
}

function normalizeClockTime(value, fallback = "09:00") {
  return /^\d{2}:\d{2}$/.test(String(value || "")) ? value : fallback;
}

function formatSunOffset(value) {
  const minutes = Number.parseInt(value || "0", 10);
  if (!Number.isFinite(minutes) || minutes === 0) {
    return "";
  }
  const sign = minutes > 0 ? "+" : "-";
  return ` ${sign} ${Math.abs(minutes)} min`;
}

function formatPeriodPoint(period = {}, point) {
  const modeKey = point === "opens" ? "opensMode" : "closesMode";
  const offsetKey = point === "opens" ? "opensOffsetMinutes" : "closesOffsetMinutes";
  const fallbackTime = point === "opens" ? "09:00" : "17:00";
  const mode = period[modeKey] || "fixed";
  if (mode === "sunrise" || mode === "sunset") {
    return `${titleCase(mode)}${formatSunOffset(period[offsetKey])}`;
  }
  return normalizeClockTime(period[point], fallbackTime);
}

function cloneState(value) {
  return JSON.parse(JSON.stringify(value || {}));
}

function addressSummary(address = {}) {
  const parts = [address.line1, address.city, address.region, address.postalCode].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "Address not set";
}

function getDateInputValue(value) {
  if (value) {
    return value;
  }
  return new Date().toISOString().slice(0, 10);
}

function dayOfYear(date) {
  const start = Date.UTC(date.getUTCFullYear(), 0, 0);
  const diff = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) - start;
  return Math.floor(diff / 86400000);
}

function normalizeDegrees(value) {
  return ((value % 360) + 360) % 360;
}

function degreesToRadians(value) {
  return value * (Math.PI / 180);
}

function radiansToDegrees(value) {
  return value * (180 / Math.PI);
}

function timezoneOffsetMinutes(date, timezone) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false
    }).formatToParts(date).reduce((map, part) => {
      map[part.type] = part.value;
      return map;
    }, {});
    const localAsUtc = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour === "24" ? "0" : parts.hour),
      Number(parts.minute),
      Number(parts.second)
    );
    return Math.round((localAsUtc - date.getTime()) / 60000);
  } catch {
    return -date.getTimezoneOffset();
  }
}

function minutesToClockLabel(totalMinutes) {
  const minutes = ((Math.round(totalMinutes) % 1440) + 1440) % 1440;
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function resolveSolarEvent(dateInput, timezone, latitude, longitude, eventType, offsetMinutes = 0) {
  const lat = Number.parseFloat(latitude);
  const lng = Number.parseFloat(longitude);
  if (!dateInput || !Number.isFinite(lat) || !Number.isFinite(lng)) {
    return "";
  }

  const date = new Date(`${dateInput}T12:00:00Z`);
  const zenith = 90.833;
  const n = dayOfYear(date);
  const lngHour = lng / 15;
  const isSunrise = eventType === "sunrise";
  const t = n + ((isSunrise ? 6 : 18) - lngHour) / 24;
  const meanAnomaly = (0.9856 * t) - 3.289;
  let trueLong = meanAnomaly
    + (1.916 * Math.sin(degreesToRadians(meanAnomaly)))
    + (0.020 * Math.sin(degreesToRadians(2 * meanAnomaly)))
    + 282.634;
  trueLong = normalizeDegrees(trueLong);

  let rightAscension = radiansToDegrees(Math.atan(0.91764 * Math.tan(degreesToRadians(trueLong))));
  rightAscension = normalizeDegrees(rightAscension);
  const trueLongQuadrant = Math.floor(trueLong / 90) * 90;
  const rightAscensionQuadrant = Math.floor(rightAscension / 90) * 90;
  rightAscension = (rightAscension + (trueLongQuadrant - rightAscensionQuadrant)) / 15;

  const sinDec = 0.39782 * Math.sin(degreesToRadians(trueLong));
  const cosDec = Math.cos(Math.asin(sinDec));
  const cosHour = (
    Math.cos(degreesToRadians(zenith)) - (sinDec * Math.sin(degreesToRadians(lat)))
  ) / (cosDec * Math.cos(degreesToRadians(lat)));

  if (cosHour > 1 || cosHour < -1) {
    return "";
  }

  let hourAngle = isSunrise
    ? 360 - radiansToDegrees(Math.acos(cosHour))
    : radiansToDegrees(Math.acos(cosHour));
  hourAngle /= 15;

  const localMeanTime = hourAngle + rightAscension - (0.06571 * t) - 6.622;
  const utcMinutes = (((localMeanTime - lngHour) % 24) + 24) % 24 * 60;
  const utcDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, utcMinutes));
  const offset = timezoneOffsetMinutes(utcDate, timezone || "America/Los_Angeles");
  return minutesToClockLabel(utcMinutes + offset + Number.parseInt(offsetMinutes || "0", 10));
}

function periodUsesSun(period = {}) {
  return ["sunrise", "sunset"].includes(period.opensMode) || ["sunrise", "sunset"].includes(period.closesMode);
}

function scheduleUsesSun(weeklyHours = {}, exceptions = []) {
  const weeklyUsesSun = Object.values(weeklyHours || {}).some((day) => (
    Array.isArray(day?.periods) && day.periods.some(periodUsesSun)
  ));
  const exceptionsUseSun = (exceptions || []).some((exception) => (
    Array.isArray(exception?.periods) && exception.periods.some(periodUsesSun)
  ));
  return weeklyUsesSun || exceptionsUseSun;
}

function consoleIconFileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      resolve("");
      return;
    }
    if (!file.type.startsWith("image/")) {
      reject(new Error("Choose a PNG, JPG, or WebP image."));
      return;
    }
    if (file.size > CONSOLE_ICON_INPUT_LIMIT_BYTES) {
      reject(new Error("Choose an image under 2 MB."));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const source = String(reader.result || "");
      const image = new Image();
      image.onload = () => {
        const scale = Math.min(1, 220 / image.width, 220 / image.height);
        const width = Math.max(1, Math.round(image.width * scale));
        const height = Math.max(1, Math.round(image.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext("2d");
        context.drawImage(image, 0, 0, width, height);
        const outputType = file.type === "image/png" ? "image/png" : "image/jpeg";
        const dataUrl = canvas.toDataURL(outputType, outputType === "image/png" ? undefined : 0.86);
        if (dataUrl.length > CONSOLE_ICON_OUTPUT_LIMIT_CHARS) {
          reject(new Error("That icon is still too large after resizing. Try a simpler square image."));
          return;
        }
        resolve(dataUrl);
      };
      image.onerror = () => reject(new Error("Could not read that image file."));
      image.src = source;
    };
    reader.onerror = () => reject(new Error("Could not read that image file."));
    reader.readAsDataURL(file);
  });
}

export function SettingsPage({
  brandingForm,
  businessHoursForm,
  businessHoursLoading,
  form,
  onSetOptionalAppVisibility,
  onSaveBranding,
  onSaveBusinessHours,
  onSaveProfile,
  savingBranding,
  savingBusinessHours,
  saving,
  selectedRange,
  setBrandingForm,
  setBusinessHoursForm,
  setForm,
  updatingProductionMenuKey = ""
}) {
  const [exceptionDraft, setExceptionDraft] = useState({
    date: "",
    closed: true,
    opens: "09:00",
    closes: "17:00",
    reason: ""
  });
  const [activeSettingsSection, setActiveSettingsSection] = useState(() => {
    if (typeof window === "undefined") {
      return "profile";
    }
    const hash = window.location.hash.replace("#settings-", "");
    if (hash === "branding") {
      return "member-portal";
    }
    return SETTINGS_SECTION_IDS.includes(hash) ? hash : "profile";
  });
  const [profileEditing, setProfileEditing] = useState(false);
  const [scheduleEditing, setScheduleEditing] = useState(false);
  const [memberPortalEditing, setMemberPortalEditing] = useState(false);
  const [consoleIconEditing, setConsoleIconEditing] = useState(false);
  const [profileDraftSnapshot, setProfileDraftSnapshot] = useState(null);
  const [scheduleDraftSnapshot, setScheduleDraftSnapshot] = useState(null);
  const [memberPortalDraftSnapshot, setMemberPortalDraftSnapshot] = useState(null);
  const [consoleIconDraftSnapshot, setConsoleIconDraftSnapshot] = useState(null);
  const [consoleIconStatus, setConsoleIconStatus] = useState({ label: "", error: "" });
  const [publicDomainState, setPublicDomainState] = useState(() => selectedRange?.publicDomain || { active: null, pending: null });
  const [domainDraft, setDomainDraft] = useState("");
  const [domainLoading, setDomainLoading] = useState(false);
  const [domainAction, setDomainAction] = useState("");
  const [domainError, setDomainError] = useState("");
  const [copiedNameserver, setCopiedNameserver] = useState("");
  useEffect(() => {
    function syncSettingsSectionFromHash() {
      const hash = window.location.hash.replace("#settings-", "");
      if (hash === "branding") {
        setActiveSettingsSection("member-portal");
        window.history.replaceState(null, "", "#settings-member-portal");
        return;
      }
      if (SETTINGS_SECTION_IDS.includes(hash)) {
        setActiveSettingsSection(hash);
      }
    }

    syncSettingsSectionFromHash();
    window.addEventListener("hashchange", syncSettingsSectionFromHash);
    return () => window.removeEventListener("hashchange", syncSettingsSectionFromHash);
  }, []);

  useEffect(() => {
    setProfileEditing(false);
    setScheduleEditing(false);
    setMemberPortalEditing(false);
    setConsoleIconEditing(false);
    setProfileDraftSnapshot(null);
    setScheduleDraftSnapshot(null);
    setMemberPortalDraftSnapshot(null);
    setConsoleIconDraftSnapshot(null);
    setConsoleIconStatus({ label: "", error: "" });
    setPublicDomainState(selectedRange?.publicDomain || { active: null, pending: null });
    setDomainDraft("");
    setDomainError("");
  }, [selectedRange?.publicDomain, selectedRange?.rangeId]);

  useEffect(() => {
    if (!selectedRange?.rangeId) {
      return undefined;
    }
    let cancelled = false;
    setDomainLoading(true);
    getRangePublicDomain(selectedRange.rangeId)
      .then((result) => {
        if (!cancelled) {
          setPublicDomainState(result.publicDomain || { active: null, pending: null });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setDomainError(err.message || "RangeGrid could not load the website domain setup.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDomainLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedRange?.rangeId]);

  if (!selectedRange) {
    return (
      <section className="panel page-panel">
        <div className="empty-state">Select a range before opening Range Setup.</div>
      </section>
    );
  }

  const rawAddress = form.address || {};
  const address = addressLooksInternal(rawAddress)
    ? loxPublicAddressFallback(selectedRange)
    : rawAddress;
  const weeklyHours = businessHoursForm.weeklyHours || {};
  const sunConfig = businessHoursForm.sun || {};
  const effectiveSunPreviewDate = getDateInputValue();
  const scheduleTimezone = businessHoursForm.timezone || form.timezone || "America/Los_Angeles";
  const sunrisePreview = resolveSolarEvent(
    effectiveSunPreviewDate,
    scheduleTimezone,
    sunConfig.latitude,
    sunConfig.longitude,
    "sunrise"
  );
  const sunsetPreview = resolveSolarEvent(
    effectiveSunPreviewDate,
    scheduleTimezone,
    sunConfig.latitude,
    sunConfig.longitude,
    "sunset"
  );
  const sunPreviewReady = Boolean(sunrisePreview && sunsetPreview);
  const hasSunScheduleRules = scheduleUsesSun(weeklyHours, businessHoursForm.exceptions || []);
  const closedDayCount = DAY_KEYS.filter((dayKey) => weeklyHours?.[dayKey]?.closed).length;
  const openDayCount = DAY_KEYS.length - closedDayCount;
  const exceptionCount = (businessHoursForm.exceptions || []).length;
  const hasAddress = Boolean(address.line1 && address.city && address.region);
  const chapterOrganization = (
    selectedRange?.config?.templateKey === "chapter_organization" ||
    selectedRange?.creationTemplate?.templateKey === "chapter_organization" ||
    String(selectedRange?.config?.vocabulary?.unit || selectedRange?.creationTemplate?.vocabulary?.unit || "").toLowerCase() === "chapter"
  );
  const publicContact = publicContactDraft(form, selectedRange);
  const hasPublicContact = Boolean(publicContact.contactEmail || publicContact.contactPhone || publicContact.website);
  const profileReady = Boolean(form.displayName && hasPublicContact && hasAddress);
  const workspaceLabel = selectedRange?.config?.vocabulary?.workspace || selectedRange?.creationTemplate?.vocabulary?.workspace || (chapterOrganization ? "Organization" : "Range");
  const workspaceLower = workspaceLabel.toLowerCase();
  const scheduleScopeLabel = chapterOrganization ? `${workspaceLower}-wide` : "range-wide";
  const addressCopyLabel = chapterOrganization ? "organization mailing address" : "physical range address";
  const memberPortalActionDefaults = Object.fromEntries(MEMBER_PORTAL_ACTIONS.map((action) => [action.key, action.defaultEnabled]));
  const memberPortalActions = {
    ...memberPortalActionDefaults,
    ...(brandingForm.memberPortal?.actions || {})
  };
  const enabledMemberPortalActionCount = MEMBER_PORTAL_ACTIONS.filter((action) => memberPortalActions[action.key]).length;
  const optionalOwnerApps = selectedRange?.config?.productionMenu?.optionalApps || {};
  const visibleOptionalOwnerAppCount = OPTIONAL_OWNER_APPS.filter((app) => optionalOwnerApps[app.key]).length;
  const activeDomain = publicDomainState.active || null;
  const pendingDomain = publicDomainState.pending || null;
  const currentDomainConfig = visibleDomainConfig(publicDomainState);
  const currentDomainStatus = domainConfigStatus(currentDomainConfig);
  const domainDisplay = currentDomainConfig?.domain || activeDomain?.domain || "Not connected";
  const isWaitingForNameserverUpdate = ["waiting_nameserver_update", "checking_dns"].includes(currentDomainStatus);
  const activeSectionCopy = {
    profile: {
      kicker: "Command identity",
      title: `${workspaceLabel} profile and public contact`,
      description: "Keep the business identity, contact information, timezone, and address accurate for member-facing screens and operational records.",
      metrics: [
        { label: "Profile readiness", value: profileReady ? "Ready" : "Needs info", detail: profileReady ? "Public identity is usable." : "Add contact and address details." },
        { label: "Public contact", value: hasPublicContact ? "Configured" : "Missing", detail: publicContact.contactEmail || publicContact.contactPhone || publicContact.website || "Email, phone, or website required." },
        { label: "Timezone", value: displayTimezone(form.timezone, "Unset"), detail: "Used for schedules and member-facing times." },
        { label: "Address", value: hasAddress ? "On file" : "Incomplete", detail: addressSummary(address) },
        { label: "Console icon", value: brandingForm.consoleIconUrl ? "Custom" : "Generated", detail: brandingForm.consoleIconFileName || "Used in owner and member console headers." }
      ]
    },
    domain: {
      kicker: "Public website",
      title: "Website domain",
      description: "Connect the owner’s domain to the RangeGrid public website without mixing it with the contact website link.",
      metrics: [
        { label: "Domain", value: domainDisplay, detail: pendingDomain ? "Pending setup" : activeDomain ? "Current website domain" : "No custom domain connected." },
        { label: "Status", value: domainStatusLabel(currentDomainStatus), detail: domainStatusDetail(currentDomainStatus) },
        { label: "Manual step", value: currentDomainConfig?.route53Nameservers?.length ? "Nameservers" : "Not ready", detail: currentDomainConfig?.route53Nameservers?.length ? "Owner updates GoDaddy once." : "Start setup to generate instructions." }
      ]
    },
    hours: {
      kicker: "Operating control",
      title: `${workspaceLabel}-wide operating schedule`,
      description: "Define the default open windows and exceptions that member-facing availability checks should respect.",
      metrics: [
        { label: "Schedule status", value: businessHoursForm.status || "Draft", detail: businessHoursLoading ? "Loading latest schedule." : "Controls reservation availability." },
        { label: "Open days", value: `${openDayCount}/7`, detail: `${closedDayCount} closed day${closedDayCount === 1 ? "" : "s"}.` },
        { label: "Exceptions", value: exceptionCount, detail: "Holiday, match-day, or special-hour overrides." },
        { label: "Timezone", value: displayTimezone(scheduleTimezone, "Unset"), detail: "Schedule evaluation timezone." },
        { label: "Sun rules", value: hasSunScheduleRules ? "In use" : "Not used", detail: hasSunScheduleRules ? `Resolved from the ${addressCopyLabel} when saved.` : "Use fixed times or choose sunrise/sunset per day." }
      ]
    },
    "member-portal": {
      kicker: "Member portal",
      title: "Member portal actions",
      description: `Choose which member-facing actions and status areas appear after someone signs in for this ${workspaceLower}.`,
      metrics: [
        { label: "Visible actions", value: enabledMemberPortalActionCount, detail: `${MEMBER_PORTAL_ACTIONS.length} portal areas available.` },
        { label: "Reservations", value: memberPortalActions.reservations ? "Shown" : "Hidden", detail: "Member booking entry points." },
        { label: "Programs", value: memberPortalActions.events ? "Shown" : "Hidden", detail: "Classes, orientations, and matches." }
      ]
    },
    "workspace-menu": {
      kicker: "Owner workspace",
      title: "Workspace menu visibility",
      description: "Keep the daily owner menu focused while allowing mature optional tools to be exposed when a range actually needs them.",
      metrics: [
        { label: "Optional apps shown", value: visibleOptionalOwnerAppCount, detail: `${OPTIONAL_OWNER_APPS.length} optional work areas available.` },
        { label: "Default menu", value: "Core apps", detail: "Daily operations, members, payments, launch, access, and setup stay visible." },
        { label: "Optional tools", value: visibleOptionalOwnerAppCount ? "Customized" : "Hidden", detail: "Armory, Leads, Reports, and Audit are opt-in." }
      ]
    }
  }[activeSettingsSection];

  const settingsSections = [
    { id: "profile", label: "Profile", meta: profileReady ? "Ready" : "Needs info" },
    { id: "domain", label: "Website Domain", meta: domainStatusLabel(currentDomainStatus) },
    { id: "hours", label: "Operating Schedule", meta: `${openDayCount} open days` },
    { id: "member-portal", label: "Member Portal", meta: `${enabledMemberPortalActionCount} actions` },
    { id: "workspace-menu", label: "Workspace Menu", meta: `${visibleOptionalOwnerAppCount} optional` }
  ];

  function setMemberPortalAction(actionKey, enabled) {
    setBrandingForm((current) => ({
      ...current,
      memberPortal: {
        ...(current.memberPortal || {}),
        actions: {
          ...memberPortalActionDefaults,
          ...(current.memberPortal?.actions || {}),
          [actionKey]: enabled
        }
      }
    }));
  }

  function setOptionalOwnerAppVisibility(appKey, visible) {
    if (typeof onSetOptionalAppVisibility === "function") {
      onSetOptionalAppVisibility(appKey, visible);
    }
  }

  function switchSettingsSection(sectionId) {
    setActiveSettingsSection(sectionId);
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `#settings-${sectionId}`);
    }
  }

  function beginProfileEdit() {
    const safeContact = publicContactDraft(form, selectedRange);
    setForm((current) => ({
      ...current,
      ...safeContact
    }));
    setProfileDraftSnapshot(cloneState({
      ...form,
      ...safeContact
    }));
    setProfileEditing(true);
  }

  function cancelProfileEdit() {
    if (profileDraftSnapshot) {
      setForm(profileDraftSnapshot);
    }
    setProfileEditing(false);
    setProfileDraftSnapshot(null);
  }

  function beginScheduleEdit() {
    setScheduleDraftSnapshot(cloneState(businessHoursForm));
    setScheduleEditing(true);
  }

  function cancelScheduleEdit() {
    if (scheduleDraftSnapshot) {
      setBusinessHoursForm(scheduleDraftSnapshot);
    }
    setScheduleEditing(false);
    setScheduleDraftSnapshot(null);
  }

  async function handleProfileSave(event) {
    await onSaveProfile(event);
    setProfileEditing(false);
    setProfileDraftSnapshot(null);
  }

  async function handleDomainSetup(event) {
    event.preventDefault();
    if (!selectedRange?.rangeId || !domainDraft.trim()) {
      return;
    }
    setDomainAction("setup");
    setDomainError("");
    try {
      const result = await setupRangePublicDomain(selectedRange.rangeId, domainDraft);
      setPublicDomainState(result.publicDomain || { active: null, pending: null });
      setDomainDraft("");
    } catch (err) {
      setDomainError(err.message || "RangeGrid could not start this domain setup.");
    } finally {
      setDomainAction("");
    }
  }

  async function handleDomainCheck(domain = "") {
    if (!selectedRange?.rangeId) {
      return;
    }
    setDomainAction("check");
    setDomainError("");
    try {
      const result = await checkRangePublicDomain(selectedRange.rangeId, domain);
      setPublicDomainState(result.publicDomain || { active: null, pending: null });
    } catch (err) {
      setDomainError(err.message || "RangeGrid could not check this domain yet.");
    } finally {
      setDomainAction("");
    }
  }

  async function handleDomainDisconnect(domain = "") {
    if (!selectedRange?.rangeId) {
      return;
    }
    setDomainAction("disconnect");
    setDomainError("");
    try {
      const result = await disconnectRangePublicDomain(selectedRange.rangeId, domain);
      setPublicDomainState(result.publicDomain || { active: null, pending: null });
    } catch (err) {
      setDomainError(err.message || "RangeGrid could not disconnect this domain.");
    } finally {
      setDomainAction("");
    }
  }

  async function copyNameserver(nameserver = "") {
    const text = String(nameserver || "").trim();
    if (!text || !navigator.clipboard) {
      return;
    }
    await navigator.clipboard.writeText(text);
    setCopiedNameserver(text);
    window.setTimeout(() => {
      setCopiedNameserver((current) => (current === text ? "" : current));
    }, 1400);
  }

  async function handleScheduleSave(event) {
    await onSaveBusinessHours(event);
    setScheduleEditing(false);
    setScheduleDraftSnapshot(null);
  }

  function updateWeeklyPeriod(dayKey, patch) {
    setBusinessHoursForm((current) => {
      const currentDay = current.weeklyHours?.[dayKey] || { closed: false, periods: [] };
      const currentPeriod = currentDay.periods?.[0] || { opens: "09:00", closes: "17:00" };
      return {
        ...current,
        weeklyHours: {
          ...current.weeklyHours,
          [dayKey]: {
            closed: false,
            periods: [{ ...currentPeriod, ...patch }]
          }
        }
      };
    });
  }

  function beginMemberPortalEdit() {
    setMemberPortalDraftSnapshot(cloneState(brandingForm));
    setMemberPortalEditing(true);
  }

  function cancelMemberPortalEdit() {
    if (memberPortalDraftSnapshot) {
      setBrandingForm(memberPortalDraftSnapshot);
    }
    setMemberPortalEditing(false);
    setMemberPortalDraftSnapshot(null);
  }

  async function handleMemberPortalSave(event) {
    await onSaveBranding(event);
    setMemberPortalEditing(false);
    setMemberPortalDraftSnapshot(null);
  }

  function beginConsoleIconEdit() {
    setConsoleIconDraftSnapshot(cloneState(brandingForm));
    setConsoleIconEditing(true);
  }

  function cancelConsoleIconEdit() {
    if (consoleIconDraftSnapshot) {
      setBrandingForm(consoleIconDraftSnapshot);
    }
    setConsoleIconEditing(false);
    setConsoleIconDraftSnapshot(null);
    setConsoleIconStatus({ label: "", error: "" });
  }

  async function handleConsoleIconSave(event) {
    await onSaveBranding(event);
    setConsoleIconEditing(false);
    setConsoleIconDraftSnapshot(null);
    setConsoleIconStatus({ label: brandingForm.consoleIconFileName || "", error: "" });
  }

  async function handleConsoleIconUpload(file) {
    if (!file) {
      return;
    }
    setConsoleIconStatus({ label: "Processing...", error: "" });
    try {
      const dataUrl = await consoleIconFileToDataUrl(file);
      setBrandingForm((current) => ({
        ...current,
        consoleIconUrl: dataUrl,
        consoleIconFileName: file.name
      }));
      setConsoleIconStatus({ label: file.name, error: "" });
    } catch (err) {
      setConsoleIconStatus({ label: "", error: err.message });
    }
  }

  const addException = () => {
    if (!exceptionDraft.date) {
      return;
    }
    const exception = {
      date: exceptionDraft.date,
      closed: exceptionDraft.closed,
      periods: exceptionDraft.closed ? [] : [{ opens: exceptionDraft.opens, closes: exceptionDraft.closes }],
      reason: exceptionDraft.reason
    };
    setBusinessHoursForm((current) => ({
      ...current,
      exceptions: [
        exception,
        ...(current.exceptions || []).filter((item) => item.date !== exception.date)
      ].sort((left, right) => left.date.localeCompare(right.date))
    }));
    setExceptionDraft((current) => ({ ...current, date: "", reason: "" }));
  };

  const removeException = (date) => {
    setBusinessHoursForm((current) => ({
      ...current,
      exceptions: (current.exceptions || []).filter((item) => item.date !== date)
    }));
  };

  return (
    <div className="settings-stack range-setup-app">
      <nav className="console-section-nav page-section-nav range-setup-tabs" aria-label="Range Setup sections">
        {settingsSections.map((section) => (
          <button
            type="button"
            className={activeSettingsSection === section.id ? "is-active" : ""}
            key={section.id}
            onClick={() => switchSettingsSection(section.id)}
          >
            <span>{section.label}</span>
            <small>{section.meta}</small>
          </button>
        ))}
      </nav>

      <section className="membership-section-overview range-setup-overview">
        <div>
          <span className="section-kicker">{activeSectionCopy.kicker}</span>
          <h3>{activeSectionCopy.title}</h3>
          <p>{activeSectionCopy.description}</p>
        </div>
        <div className="membership-context-metrics range-setup-metrics">
          {activeSectionCopy.metrics.map((metric) => (
            <div key={metric.label}>
              <span>{metric.label}</span>
              <strong>{metric.value}</strong>
              <small>{metric.detail}</small>
            </div>
          ))}
        </div>
      </section>

      {activeSettingsSection === "profile" && (
        <section className="panel page-panel range-setup-workbench">
          <div className="range-setup-form">
            <div className="requirements-workbench-header range-setup-workbench-header">
              <div>
                <h3>{workspaceLabel} profile</h3>
                <p>Review the public identity first. Edit only when the owner needs to change what members and staff see.</p>
              </div>
              <div className="range-setup-header-actions">
                {profileEditing ? (
                  <>
                    <button className="secondary-button" type="button" onClick={cancelProfileEdit}>
                      Cancel
                    </button>
                    <button type="submit" form="range-profile-editor" disabled={saving || !form.displayName.trim()}>
                      {saving ? "Saving..." : "Save profile"}
                    </button>
                  </>
                ) : (
                  <button type="button" onClick={beginProfileEdit}>
                    Edit profile
                  </button>
                )}
              </div>
            </div>

            {profileEditing ? (
              <form id="range-profile-editor" className="range-setup-edit-surface" onSubmit={handleProfileSave}>
                <div className="requirements-policy-grid range-setup-card-grid">
                  <section className="requirements-policy-card range-setup-card">
                    <div className="requirements-policy-heading">
                      <span>Identity</span>
                      <h4>Business identity</h4>
                      <p>Name, legal identity, and timezone used across RangeGrid.</p>
                    </div>
                    <label>
                      Display name
                      <input
                        value={form.displayName}
                        onChange={(event) => setForm((current) => ({ ...current, displayName: event.target.value }))}
                        placeholder={selectedRange.name}
                        required
                      />
                    </label>
                    <label>
                      Legal name
                      <input
                        value={form.legalName}
                        onChange={(event) => setForm((current) => ({ ...current, legalName: event.target.value }))}
                        placeholder="Oak Ridge Shooting Club LLC"
                      />
                    </label>
                    <label>
                      Timezone
                      <select
                        value={form.timezone}
                        onChange={(event) => setForm((current) => ({ ...current, timezone: event.target.value }))}
                      >
                        <option value="America/Los_Angeles">America/Los_Angeles</option>
                        <option value="America/Denver">America/Denver</option>
                        <option value="America/Chicago">America/Chicago</option>
                        <option value="America/New_York">America/New_York</option>
                      </select>
                    </label>
                  </section>

                  <section className="requirements-policy-card range-setup-card">
                    <div className="requirements-policy-heading">
                      <span>Contact</span>
                      <h4>Public contact</h4>
                      <p>What members see when they need to reach the {workspaceLower}.</p>
                    </div>
                    <label>
                      Contact email
                      <input
                        type="email"
                        value={form.contactEmail}
                        onChange={(event) => setForm((current) => ({ ...current, contactEmail: event.target.value }))}
                        placeholder="frontdesk@example.com"
                      />
                    </label>
                    <label>
                      Contact phone
                      <input
                        value={form.contactPhone}
                        onChange={(event) => setForm((current) => ({ ...current, contactPhone: event.target.value }))}
                        placeholder="555-0100"
                      />
                    </label>
                    <label>
                      Website
                      <input
                        value={form.website}
                        onChange={(event) => setForm((current) => ({ ...current, website: event.target.value }))}
                        placeholder="https://example.com"
                      />
                    </label>
                  </section>

                  <section className="requirements-policy-card requirements-wide-card range-setup-card">
                    <div className="requirements-policy-heading">
                      <span>Location</span>
                      <h4>Public address</h4>
                      <p>Use the {addressCopyLabel} members should recognize.</p>
                    </div>
                    <div className="range-setup-field-grid">
                      <label>
                        Address line 1
                        <input
                          value={address.line1 || ""}
                          onChange={(event) => setForm((current) => ({
                            ...current,
                            address: { ...current.address, line1: event.target.value }
                          }))}
                        />
                      </label>
                      <label>
                        Address line 2
                        <input
                          value={address.line2 || ""}
                          onChange={(event) => setForm((current) => ({
                            ...current,
                            address: { ...current.address, line2: event.target.value }
                          }))}
                        />
                      </label>
                      <label>
                        City
                        <input
                          value={address.city || ""}
                          onChange={(event) => setForm((current) => ({
                            ...current,
                            address: { ...current.address, city: event.target.value }
                          }))}
                        />
                      </label>
                      <label>
                        State or region
                        <input
                          value={address.region || ""}
                          onChange={(event) => setForm((current) => ({
                            ...current,
                            address: { ...current.address, region: event.target.value }
                          }))}
                        />
                      </label>
                      <label>
                        Postal code
                        <input
                          value={address.postalCode || ""}
                          onChange={(event) => setForm((current) => ({
                            ...current,
                            address: { ...current.address, postalCode: event.target.value }
                          }))}
                        />
                      </label>
                      <label>
                        Country
                        <input
                          value={address.country || ""}
                          onChange={(event) => setForm((current) => ({
                            ...current,
                            address: { ...current.address, country: event.target.value.toUpperCase().slice(0, 2) }
                          }))}
                          placeholder="US"
                        />
                      </label>
                    </div>
                  </section>

                  <section className="requirements-policy-card requirements-wide-card range-setup-card">
                    <div className="requirements-policy-heading">
                      <span>Public copy</span>
                      <h4>Public range intro</h4>
                      <p>This is the short member-facing description shown on public signup and member console pages.</p>
                    </div>
                    <label>
                      Member-facing intro text
                      <textarea
                        value={form.description}
                        onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                        placeholder="Example: Outdoor long-range facility with member reservations, safety orientation, and seasonal event access."
                      />
                    </label>
                  </section>
                </div>
              </form>
            ) : (
              <div className="range-setup-read-model">
                <section className="range-setup-detail-card is-primary">
                  <div className="requirements-policy-heading">
                    <span>Identity</span>
                    <h4>{displayValue(form.displayName, selectedRange.name)}</h4>
                    <p>{displayValue(form.description, "No member-facing intro has been added yet.")}</p>
                  </div>
                  <div className="range-setup-detail-list">
                    <div>
                      <span>Legal name</span>
                      <strong>{displayValue(form.legalName)}</strong>
                    </div>
                    <div>
                      <span>Timezone</span>
                      <strong>{displayTimezone(form.timezone)}</strong>
                    </div>
                  </div>
                </section>

                <section className="range-setup-detail-card">
                  <div className="requirements-policy-heading">
                    <span>Contact</span>
                    <h4>Public contact</h4>
                    <p>Shown to members and prospects when they need to reach the {workspaceLower}.</p>
                  </div>
                  <div className="range-setup-detail-list">
                    <div>
                      <span>Email</span>
                      <strong>{displayValue(publicContact.contactEmail)}</strong>
                    </div>
                    <div>
                      <span>Phone</span>
                      <strong>{displayValue(publicContact.contactPhone)}</strong>
                    </div>
                    <div>
                      <span>Website</span>
                      <strong>{displayValue(publicContact.website)}</strong>
                    </div>
                  </div>
                </section>

                <section className="range-setup-detail-card">
                  <div className="requirements-policy-heading">
                    <span>Location</span>
                    <h4>{chapterOrganization ? "Organization address" : "Range address"}</h4>
                    <p>{addressSummary(address)}</p>
                  </div>
                  <div className="range-setup-detail-list">
                    <div>
                      <span>Line 2</span>
                      <strong>{displayValue(address.line2)}</strong>
                    </div>
                    <div>
                      <span>Country</span>
                      <strong>{displayValue(address.country, "US")}</strong>
                    </div>
                  </div>
                </section>
              </div>
            )}

            <section className={`range-setup-console-icon-card ${consoleIconEditing ? "is-editing" : ""}`}>
              <div className="range-setup-console-icon-preview" aria-hidden="true">
                {brandingForm.consoleIconUrl ? (
                  <img src={brandingForm.consoleIconUrl} alt="" />
                ) : (
                  brandingForm.shortName || "RG"
                )}
              </div>
              <div className="range-setup-console-icon-copy">
                <span>Console icon</span>
                <strong>{brandingForm.consoleIconUrl ? "Custom icon selected" : `Using generated ${workspaceLower} mark`}</strong>
                <small>
                  {brandingForm.consoleIconFileName || consoleIconStatus.label || "Used in owner navigation and the member console header."}
                </small>
                {consoleIconStatus.error && <small className="range-setup-icon-error">{consoleIconStatus.error}</small>}
              </div>
              {consoleIconEditing ? (
                <form className="range-setup-console-icon-actions" onSubmit={handleConsoleIconSave}>
                  <label className="range-setup-file-button">
                    Upload icon
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={(event) => handleConsoleIconUpload(event.target.files?.[0])}
                    />
                  </label>
                  {brandingForm.consoleIconUrl && (
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => setBrandingForm((current) => ({ ...current, consoleIconUrl: "", consoleIconFileName: "" }))}
                    >
                      Use mark
                    </button>
                  )}
                  <button className="secondary-button" type="button" onClick={cancelConsoleIconEdit}>
                    Cancel
                  </button>
                  <button type="submit" disabled={savingBranding}>
                    {savingBranding ? "Saving..." : "Save icon"}
                  </button>
                </form>
              ) : (
                <button type="button" onClick={beginConsoleIconEdit}>
                  Edit icon
                </button>
              )}
            </section>
          </div>
        </section>
      )}

      {activeSettingsSection === "domain" && (
        <section className="panel page-panel range-setup-workbench">
          <div className="range-setup-form">
            <div className="requirements-workbench-header range-setup-workbench-header">
              <div>
                <h3>Website domain</h3>
                <p>Connect the public website to the owner’s domain. GoDaddy stays the registrar; RangeGrid manages the website DNS after nameservers are changed.</p>
              </div>
              <div className="range-setup-header-actions">
                {currentDomainConfig && (
                  <button
                    type="button"
                    className="secondary-button"
                    disabled={domainAction === "check"}
                    onClick={() => handleDomainCheck(currentDomainConfig.domain)}
                  >
                    {domainAction === "check" ? "Checking..." : "Check again"}
                  </button>
                )}
              </div>
            </div>

            <div className="range-domain-layout">
              <section className={`range-domain-status-card is-${currentDomainStatus.replace(/_/g, "-")}`}>
                <span>{pendingDomain ? "Pending domain" : activeDomain ? "Connected domain" : "Website domain"}</span>
                <strong>{domainDisplay}</strong>
                <p>{domainStatusDetail(currentDomainStatus)}</p>
                {activeDomain?.status === "live" && (
                  <div className="range-domain-live-links">
                    <a href={`https://${activeDomain.domain}`} target="_blank" rel="noreferrer">Open website</a>
                    <a href={`https://${activeDomain.wwwDomain}`} target="_blank" rel="noreferrer">Open www</a>
                  </div>
                )}
                {currentDomainConfig?.lastError && (
                  <div className="range-domain-alert">{currentDomainConfig.lastError}</div>
                )}
                {domainError && <div className="range-domain-alert">{domainError}</div>}
              </section>

              <section className="range-setup-detail-card range-domain-connect-card">
                <div className="requirements-policy-heading">
                  <span>Connect</span>
                  <h4>{activeDomain ? "Change domain" : "Connect your domain"}</h4>
                  <p>Use the root domain. RangeGrid will also prepare the www version.</p>
                </div>
                <form className="range-domain-form" onSubmit={handleDomainSetup}>
                  <label>
                    Domain
                    <input
                      value={domainDraft}
                      onChange={(event) => setDomainDraft(event.target.value)}
                      placeholder="libertylongrange.com"
                    />
                  </label>
                  <button type="submit" disabled={domainAction === "setup" || !domainDraft.trim()}>
                    {domainAction === "setup" ? "Preparing..." : activeDomain ? "Prepare new domain" : "Start setup"}
                  </button>
                </form>
              </section>
            </div>

            {currentDomainConfig?.route53Nameservers?.length > 0 && (
              <section className="range-setup-detail-card range-domain-nameserver-card">
                <div className="requirements-policy-heading">
                  <span>GoDaddy step</span>
                  <h4>Change nameservers in GoDaddy</h4>
                  <p>Replace the current nameservers with these four values. Then return here and ask RangeGrid to check again.</p>
                </div>
                <div className="range-domain-nameserver-list">
                  {currentDomainConfig.route53Nameservers.map((nameserver) => (
                    <div className="range-domain-nameserver-row" key={nameserver}>
                      <code>{nameserver}</code>
                      <button type="button" className="secondary-button" onClick={() => copyNameserver(nameserver)}>
                        {copiedNameserver === nameserver ? "Copied" : "Copy"}
                      </button>
                    </div>
                  ))}
                </div>
                <div className="range-domain-actions">
                  {isWaitingForNameserverUpdate ? (
                    <button
                      type="button"
                      disabled={domainAction === "check"}
                      onClick={() => handleDomainCheck(currentDomainConfig.domain)}
                    >
                      {domainAction === "check" ? "Checking..." : "I updated nameservers"}
                    </button>
                  ) : (
                    <span className="range-domain-step-complete">Nameservers verified</span>
                  )}
                </div>
              </section>
            )}

            {(activeDomain || pendingDomain) && (
              <section className="range-setup-detail-card range-domain-technical-card">
                <div className="requirements-policy-heading">
                  <span>Control</span>
                  <h4>Domain management</h4>
                  <p>Disconnect removes the RangeGrid mapping. DNS and AWS cleanup can be handled by an operator if needed.</p>
                </div>
                <div className="range-domain-actions">
                  {pendingDomain && (
                    <button
                      type="button"
                      className="secondary-button"
                      disabled={domainAction === "disconnect"}
                      onClick={() => handleDomainDisconnect(pendingDomain.domain)}
                    >
                      Remove pending setup
                    </button>
                  )}
                  {activeDomain && (
                    <button
                      type="button"
                      className="danger-button"
                      disabled={domainAction === "disconnect"}
                      onClick={() => handleDomainDisconnect(activeDomain.domain)}
                    >
                      Disconnect domain
                    </button>
                  )}
                </div>
                <details className="range-domain-technical-details">
                  <summary>Technical details</summary>
                  <div className="range-setup-detail-list">
                    <div>
                      <span>Hosted zone</span>
                      <strong>{displayValue(currentDomainConfig?.technical?.hostedZoneId)}</strong>
                    </div>
                    <div>
                      <span>Certificate</span>
                      <strong>{displayValue(currentDomainConfig?.technical?.certificateArn)}</strong>
                    </div>
                    <div>
                      <span>Distribution</span>
                      <strong>{displayValue(currentDomainConfig?.technical?.cloudFrontDistributionId)}</strong>
                    </div>
                    <div>
                      <span>Last checked</span>
                      <strong>{displayValue(currentDomainConfig?.lastCheckedAt)}</strong>
                    </div>
                  </div>
                </details>
              </section>
            )}

            {domainLoading && <div className="range-setup-compact-empty">Loading website domain setup...</div>}
          </div>
        </section>
      )}

      {activeSettingsSection === "hours" && (
        <section className="panel page-panel range-setup-workbench">
          <div className="range-setup-form">
            <div className="requirements-workbench-header range-setup-workbench-header">
              <div>
                <h3>Operating schedule</h3>
                <p>Review normal {workspaceLower} hours first. Open the editor only when the schedule needs a real change.</p>
              </div>
              <div className="range-setup-header-actions">
                {scheduleEditing ? (
                  <>
                    <button className="secondary-button" type="button" onClick={cancelScheduleEdit}>
                      Cancel
                    </button>
                    <button type="submit" form="range-schedule-editor" disabled={savingBusinessHours || businessHoursLoading}>
                      {savingBusinessHours ? "Saving..." : businessHoursLoading ? "Loading..." : "Save schedule"}
                    </button>
                  </>
                ) : (
                  <button type="button" onClick={beginScheduleEdit} disabled={businessHoursLoading}>
                    {businessHoursLoading ? "Loading..." : "Edit schedule"}
                  </button>
                )}
              </div>
            </div>

            {scheduleEditing ? (
              <form id="range-schedule-editor" className="range-setup-edit-surface" onSubmit={handleScheduleSave}>
                <div className="requirements-policy-grid range-setup-card-grid range-setup-schedule-stack">
                  <section className="requirements-policy-card range-setup-card">
                    <div className="requirements-policy-heading">
                      <span>Control</span>
                      <h4>Schedule state</h4>
                      <p>Keep draft changes out of availability checks until ready.</p>
                    </div>
                    <label>
                      Schedule status
                      <select
                        value={businessHoursForm.status}
                        onChange={(event) => setBusinessHoursForm((current) => ({ ...current, status: event.target.value }))}
                      >
                        <option value="active">Active</option>
                        <option value="draft">Draft</option>
                      </select>
                    </label>
                    <label>
                      Schedule timezone
                      <select
                        value={businessHoursForm.timezone}
                        onChange={(event) => setBusinessHoursForm((current) => ({ ...current, timezone: event.target.value }))}
                      >
                        <option value="America/Los_Angeles">America/Los_Angeles</option>
                        <option value="America/Denver">America/Denver</option>
                        <option value="America/Chicago">America/Chicago</option>
                        <option value="America/New_York">America/New_York</option>
                      </select>
                    </label>
                    <label>
                      Notes
                      <textarea
                        value={businessHoursForm.notes || ""}
                        onChange={(event) => setBusinessHoursForm((current) => ({ ...current, notes: event.target.value }))}
                        placeholder="Holiday exceptions, seasonal closures, or schedule notes."
                      />
                    </label>
                    <div className="range-setup-sun-panel">
                      <div>
                        <span>Sunrise and sunset rules</span>
                        <strong>{hasSunScheduleRules ? `Resolved from ${workspaceLower} profile` : "Available per day"}</strong>
                        <small>Choose sunrise or sunset in the weekly rhythm. RangeGrid uses the {addressCopyLabel} and timezone to calculate the real clock time each day.</small>
                      </div>
                    </div>
                  </section>

                  <section className="requirements-policy-card requirements-wide-card range-setup-card range-setup-week-card">
                    <div className="requirements-policy-heading">
                      <span>Weekly rhythm</span>
                      <h4>Default open windows</h4>
                      <p>Set the normal operating pattern before adding one-off exceptions.</p>
                    </div>
                    <div className="range-setup-week-grid">
                      {DAY_KEYS.map((dayKey) => {
                        const day = weeklyHours?.[dayKey] || { closed: true, periods: [] };
                        const period = day.periods?.[0] || { opens: "09:00", closes: "17:00" };
                        return (
                          <div className={`range-setup-day-card ${day.closed ? "is-closed" : "is-open"}`} key={dayKey}>
                            <div className="range-setup-day-header">
                              <strong>{titleCase(dayKey)}</strong>
                              <span>{formatDayWindow(day)}</span>
                            </div>
                            <label className="inline-check">
                              <input
                                type="checkbox"
                                checked={Boolean(day.closed)}
                                onChange={(event) => setBusinessHoursForm((current) => ({
                                  ...current,
                                  weeklyHours: {
                                    ...current.weeklyHours,
                                    [dayKey]: {
                                      closed: event.target.checked,
                                      periods: event.target.checked ? [] : [period]
                                    }
                                  }
                                }))}
                              />
                              Closed
                            </label>
                            <div className="range-setup-time-pair">
                              <label className="range-setup-sun-control">
                                <span>Opens</span>
                                <select
                                  value={period.opensMode || "fixed"}
                                  disabled={Boolean(day.closed)}
                                  onChange={(event) => updateWeeklyPeriod(dayKey, { opensMode: event.target.value })}
                                >
                                  <option value="fixed">Fixed time</option>
                                  <option value="sunrise">Sunrise</option>
                                  <option value="sunset">Sunset</option>
                                </select>
                                {(period.opensMode || "fixed") === "fixed" ? (
                                  <input
                                    type="time"
                                    value={normalizeClockTime(period.opens, "09:00")}
                                    disabled={Boolean(day.closed)}
                                    onChange={(event) => updateWeeklyPeriod(dayKey, { opens: event.target.value, opensMode: "fixed" })}
                                  />
                                ) : (
                                  <div className="input-with-suffix">
                                    <input
                                      type="number"
                                      value={period.opensOffsetMinutes || "0"}
                                      disabled={Boolean(day.closed)}
                                      onChange={(event) => updateWeeklyPeriod(dayKey, { opensOffsetMinutes: event.target.value })}
                                    />
                                    <span>min</span>
                                  </div>
                                )}
                                <small>{formatPeriodPoint(period, "opens")}</small>
                              </label>
                              <label className="range-setup-sun-control">
                                <span>Closes</span>
                                <select
                                  value={period.closesMode || "fixed"}
                                  disabled={Boolean(day.closed)}
                                  onChange={(event) => updateWeeklyPeriod(dayKey, { closesMode: event.target.value })}
                                >
                                  <option value="fixed">Fixed time</option>
                                  <option value="sunrise">Sunrise</option>
                                  <option value="sunset">Sunset</option>
                                </select>
                                {(period.closesMode || "fixed") === "fixed" ? (
                                  <input
                                    type="time"
                                    value={normalizeClockTime(period.closes, "17:00")}
                                    disabled={Boolean(day.closed)}
                                    onChange={(event) => updateWeeklyPeriod(dayKey, { closes: event.target.value, closesMode: "fixed" })}
                                  />
                                ) : (
                                  <div className="input-with-suffix">
                                    <input
                                      type="number"
                                      value={period.closesOffsetMinutes || "0"}
                                      disabled={Boolean(day.closed)}
                                      onChange={(event) => updateWeeklyPeriod(dayKey, { closesOffsetMinutes: event.target.value })}
                                    />
                                    <span>min</span>
                                  </div>
                                )}
                                <small>{formatPeriodPoint(period, "closes")}</small>
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>

                  <section className="requirements-policy-card requirements-wide-card range-setup-card">
                    <div className="requirements-policy-heading">
                      <span>Override</span>
                      <h4>Exceptions</h4>
                      <p>Add holidays, match-day closures, maintenance days, or special one-day hours.</p>
                    </div>
                    <div className="settings-exception-row range-setup-exception-row">
                      <label>
                        Date
                        <input
                          type="date"
                          value={exceptionDraft.date}
                          onChange={(event) => setExceptionDraft((current) => ({ ...current, date: event.target.value }))}
                        />
                      </label>
                      <label className="inline-check">
                        <input
                          type="checkbox"
                          checked={exceptionDraft.closed}
                          onChange={(event) => setExceptionDraft((current) => ({ ...current, closed: event.target.checked }))}
                        />
                        Closed
                      </label>
                      <label>
                        Opens
                        <input
                          type="time"
                          value={exceptionDraft.opens}
                          disabled={exceptionDraft.closed}
                          onChange={(event) => setExceptionDraft((current) => ({ ...current, opens: event.target.value }))}
                        />
                      </label>
                      <label>
                        Closes
                        <input
                          type="time"
                          value={exceptionDraft.closes}
                          disabled={exceptionDraft.closed}
                          onChange={(event) => setExceptionDraft((current) => ({ ...current, closes: event.target.value }))}
                        />
                      </label>
                      <label>
                        Reason
                        <input
                          value={exceptionDraft.reason}
                          onChange={(event) => setExceptionDraft((current) => ({ ...current, reason: event.target.value }))}
                          placeholder="Holiday closure"
                        />
                      </label>
                      <button type="button" onClick={addException} disabled={!exceptionDraft.date}>
                        Add exception
                      </button>
                    </div>
                    {(businessHoursForm.exceptions || []).length > 0 ? (
                      <div className="settings-exception-list range-setup-exception-list">
                        {businessHoursForm.exceptions.map((exception) => (
                          <div key={exception.date}>
                            <strong>{exception.date}</strong>
                            <span>
                              {exception.closed
                                ? "Closed"
                                : `${exception.periods?.[0]?.opens || "09:00"}-${exception.periods?.[0]?.closes || "17:00"}`}
                              {exception.reason ? ` / ${exception.reason}` : ""}
                            </span>
                            <button type="button" onClick={() => removeException(exception.date)}>Remove</button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="review-queue-status is-clear">
                        <span>Clear</span>
                        <strong>No exceptions scheduled</strong>
                        <small>Use this area for holidays, major programs, or maintenance days.</small>
                      </div>
                    )}
                  </section>
                </div>
              </form>
            ) : (
              <div className="range-setup-read-model">
                <section className="range-setup-detail-card is-primary">
                  <div className="requirements-policy-heading">
                    <span>Control</span>
                    <h4>{titleCase(businessHoursForm.status || "Draft")}</h4>
                    <p>{displayValue(businessHoursForm.notes, `No ${scheduleScopeLabel} schedule notes on file.`)}</p>
                  </div>
                  <div className="range-setup-detail-list">
                    <div>
                      <span>Timezone</span>
                      <strong>{displayTimezone(businessHoursForm.timezone || form.timezone)}</strong>
                    </div>
                    <div>
                      <span>Open days</span>
                      <strong>{openDayCount}/7</strong>
                    </div>
                    <div>
                      <span>Sunrise / sunset</span>
                      <strong>{hasSunScheduleRules ? "In schedule" : "Not used"}</strong>
                      <small>{hasSunScheduleRules ? (sunPreviewReady ? `Dynamic sun rules are ready from the ${workspaceLower} profile.` : `Add a complete ${addressCopyLabel} so sun rules can resolve for operations.`) : "Select sunrise or sunset in edit mode when needed."}</small>
                    </div>
                  </div>
                </section>

                <section className="range-setup-detail-card is-wide">
                  <div className="requirements-policy-heading">
                    <span>Weekly rhythm</span>
                    <h4>Default open windows</h4>
                    <p>These are the normal operating windows used before resource-specific rules and exceptions.</p>
                  </div>
                  <div className="range-setup-week-summary">
                    {DAY_KEYS.map((dayKey) => {
                      const day = weeklyHours?.[dayKey] || { closed: true, periods: [] };
                      return (
                        <div className={day.closed ? "is-closed" : "is-open"} key={dayKey}>
                          <strong>{titleCase(dayKey)}</strong>
                          <span>{formatDayWindow(day)}</span>
                        </div>
                      );
                    })}
                  </div>
                </section>

                <section className="range-setup-detail-card">
                  <div className="requirements-policy-heading">
                    <span>Exceptions</span>
                    <h4>{exceptionCount ? `${exceptionCount} override${exceptionCount === 1 ? "" : "s"}` : "No exceptions"}</h4>
                    <p>Holiday, match-day, or maintenance-day changes appear here.</p>
                  </div>
                  {(businessHoursForm.exceptions || []).length > 0 ? (
                    <div className="range-setup-exception-read-list">
                      {businessHoursForm.exceptions.map((exception) => (
                        <div key={exception.date}>
                          <strong>{exception.date}</strong>
                          <span>
                            {exception.closed
                              ? "Closed"
                              : `${exception.periods?.[0]?.opens || "09:00"}-${exception.periods?.[0]?.closes || "17:00"}`}
                            {exception.reason ? ` / ${exception.reason}` : ""}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="review-queue-status is-clear range-setup-compact-empty">
                      <span>Clear</span>
                      <strong>No exceptions scheduled</strong>
                    </div>
                  )}
                </section>
              </div>
            )}
          </div>
        </section>
      )}

      {activeSettingsSection === "member-portal" && (
        <section className="panel page-panel range-setup-workbench">
          <div className="range-setup-form">
            <div className="requirements-workbench-header range-setup-workbench-header">
              <div>
                <h3>Member portal actions</h3>
                <p>Review the member-facing actions first. Edit only when the {workspaceLower} is changing what members can open.</p>
              </div>
              <div className="range-setup-header-actions">
                {memberPortalEditing ? (
                  <>
                    <button className="secondary-button" type="button" onClick={cancelMemberPortalEdit}>
                      Cancel
                    </button>
                    <button type="submit" form="member-portal-actions-editor" disabled={savingBranding}>
                      {savingBranding ? "Saving..." : "Save actions"}
                    </button>
                  </>
                ) : (
                  <button type="button" onClick={beginMemberPortalEdit}>
                    Edit actions
                  </button>
                )}
              </div>
            </div>

            <section className={`requirements-policy-card range-setup-card member-portal-action-card ${memberPortalEditing ? "is-editing" : ""}`}>
              <div className="requirements-policy-heading">
                <span>Portal controls</span>
                <h4>Visible member actions</h4>
                <p>
                  {memberPortalEditing
                    ? "Turn on the actions members should see in their portal."
                    : `Current member-facing actions shown after someone signs in for this ${workspaceLower}.`}
                </p>
              </div>
              {memberPortalEditing ? (
                <form id="member-portal-actions-editor" className="member-portal-action-list" onSubmit={handleMemberPortalSave}>
                  {MEMBER_PORTAL_ACTIONS.map((action) => (
                    <label className="member-portal-action-row" key={action.key}>
                      <input
                        type="checkbox"
                        checked={Boolean(memberPortalActions[action.key])}
                        onChange={(event) => setMemberPortalAction(action.key, event.target.checked)}
                      />
                      <span>
                        <strong>{action.label}</strong>
                        <small>{action.detail}</small>
                      </span>
                    </label>
                  ))}
                </form>
              ) : (
                <div className="member-portal-action-read-list">
                  {MEMBER_PORTAL_ACTIONS.map((action) => {
                    const enabled = Boolean(memberPortalActions[action.key]);
                    return (
                      <div className={enabled ? "is-enabled" : "is-hidden"} key={action.key}>
                        <span>{enabled ? "Shown" : "Hidden"}</span>
                        <strong>{action.label}</strong>
                        <small>{action.detail}</small>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

          </div>
        </section>
      )}

      {activeSettingsSection === "workspace-menu" && (
        <section className="panel page-panel range-setup-workbench">
          <div className="range-setup-form">
            <div className="requirements-workbench-header range-setup-workbench-header">
              <div>
                <h3>Workspace menu visibility</h3>
                <p>Start owners with the core operating apps. Show optional tools only when they match how this range is actually run.</p>
              </div>
            </div>

            <section className="requirements-policy-card range-setup-card member-portal-action-card">
              <div className="requirements-policy-heading">
                <span>Optional tools</span>
                <h4>Owner menu add-ons</h4>
                <p>These work areas stay hidden from the main menu until the owner chooses to show them.</p>
              </div>
              <div className="member-portal-action-read-list workspace-menu-action-list">
                {OPTIONAL_OWNER_APPS.map((app) => {
                  const isVisible = Boolean(optionalOwnerApps[app.key]);
                  const isSaving = updatingProductionMenuKey === app.key;
                  return (
                    <div className={isVisible ? "is-enabled" : "is-hidden"} key={app.key}>
                      <span>{isVisible ? "Shown" : "Hidden"}</span>
                      <strong>{app.label}</strong>
                      <small>{app.detail}</small>
                      <button
                        type="button"
                        className={isVisible ? "secondary-button" : ""}
                        disabled={isSaving}
                        onClick={() => setOptionalOwnerAppVisibility(app.key, !isVisible)}
                      >
                        {isSaving ? "Saving..." : isVisible ? "Hide from menu" : "Show in menu"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        </section>
      )}
    </div>
  );
}
